import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { ShieldCheck, FileText, AlertTriangle, ExternalLink, FlaskConical } from "lucide-react";

export default function CoaVerifyPage() {
  const { code } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/coa/lookup/${encodeURIComponent(code)}`)
      .then((r) => setResult(r.data))
      .catch(() => setResult({ found: false }))
      .finally(() => setLoading(false));
  }, [code]);

  const coa = result?.found ? result.coa : null;
  const docHref = coa ? `${API}${coa.document_url.startsWith("/api") ? coa.document_url.slice(4) : coa.document_url}` : "";

  return (
    <div className="min-h-screen bg-[#06070A] flex flex-col">
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-4 py-24" data-testid="coa-verify-page">
        {loading ? (
          <div className="w-10 h-10 border-2 border-[#00AEEF] border-t-transparent rounded-full animate-spin" />
        ) : coa ? (
          <div className="w-full max-w-lg glass-dark rounded-2xl p-8 sm:p-10" data-testid="coa-verify-found">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-emerald-400 text-xs font-semibold uppercase tracking-wider">Verified Laboratory Document</p>
                <h1 className="text-2xl font-bold text-white">{coa.product_name}</h1>
              </div>
            </div>
            <div className="space-y-3 mb-8 text-sm">
              {[
                ["Concentration", coa.concentration],
                ["Batch", coa.batch_id],
                ["Report #", coa.report_id],
                ["Laboratory", coa.laboratory],
                ["Report Date", coa.report_date],
                ["Purity (HPLC)", coa.purity ? `${coa.purity}%` : ""],
              ].filter(([, v]) => v).map(([label, value]) => (
                <div key={label} className="flex justify-between gap-4 border-b border-white/5 pb-2.5">
                  <span className="text-slate-500">{label}</span>
                  <span className="text-white font-mono text-right" data-testid={`coa-verify-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{value}</span>
                </div>
              ))}
            </div>
            <a href={docHref} target="_blank" rel="noopener noreferrer"
              className="w-full btn-primary py-3.5 flex items-center justify-center gap-2"
              data-testid="coa-verify-view-pdf">
              <FileText className="w-5 h-5" /> View COA (PDF) <ExternalLink className="w-4 h-4 opacity-60" />
            </a>
            <p className="text-[11px] text-slate-600 mt-6 text-center leading-relaxed">
              All products are intended exclusively for in-vitro laboratory research use. Not for human consumption.
            </p>
          </div>
        ) : (
          <div className="w-full max-w-lg glass-dark rounded-2xl p-8 sm:p-10 text-center" data-testid="coa-verify-notfound">
            <div className="w-14 h-14 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto mb-5">
              <AlertTriangle className="w-7 h-7 text-amber-400" />
            </div>
            <h1 className="text-xl font-bold text-white mb-3">Document Not Found</h1>
            <p className="text-slate-400 text-sm leading-relaxed mb-2">
              No published COA matches <span className="font-mono text-[#00AEEF]">{code}</span>.
            </p>
            <p className="text-slate-500 text-xs leading-relaxed mb-8">
              The documentation for this batch may be in transition to our current laboratory partnership.
              You can request the current COA from our team.
            </p>
            <Link to="/verify" className="btn-primary inline-flex items-center gap-2 px-8 py-3" data-testid="coa-verify-goto-library">
              <FlaskConical className="w-4 h-4" /> Open COA Library
            </Link>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
